import { Injectable } from '@angular/core';
import { getPublicClient, readContract } from '@wagmi/core';

import { Observable, from } from 'rxjs';
import { encodeFunctionData, getAddress, isAddress } from 'viem';
import { environment } from 'src/environments/environment';
import { ContractUtils } from '../utils/contract-utils';
import { AppkitService } from './appkit.service';


const erc20Abi = require('../abis/erc20.json');

@Injectable({
  providedIn: 'root'
})
export class Erc20ContractService {

  publicClient = getPublicClient(this.appkitService.adapter.wagmiConfig);

  constructor(private contractUtils: ContractUtils, private appkitService: AppkitService) { }

  async approve({ chainId, walletAddress }: any, tokenAddress: string, { functionName, args }: any) {
    let contractParams = {
      abi: erc20Abi,
      functionName,
      args
    }
    let requiredGas = await this.publicClient?.estimateContractGas({
      address: await getAddress(tokenAddress),
      account: walletAddress,
      ...contractParams
    });
    let approveAbi = await encodeFunctionData({
      ...contractParams
    })
    return { approveAbi, requiredGas }
  }

  async getDecimal(chainId: number, contractAddress: string) {
    try {
      return await readContract(this.appkitService.adapter.wagmiConfig, {
        address: await getAddress(contractAddress),
        abi: erc20Abi,
        chainId,
        functionName: 'decimals',
        args: []
      });
    } catch (e) {
      // On-chain read failed (RPC error/timeout). Fall back to the environment's
      // token decimals so allowance/amount math stays correct: prod uses 6-dp
      // USDC, dev/staging use an 18-dp test token. (Persisting per-currency
      // decimals would remove this assumption — future improvement.)
      return environment.production ? 6 : 18;
    }
  }
  getContractName(chainId: number, address: string): Observable<string> {
    return from(this._getContractName(chainId, address));
  }

  async _getContractName(chainId: number, address: string): Promise<string> {
    // Transaction-log rows pass arbitrary "from"/"to" values here — not always
    // a valid address (user ids, sentinel "-", lowercase or mis-checksummed
    // strings). viem's getAddress is strict and throws synchronously inside
    // this async function; without a guard the rejection escapes the caller's
    // `catchError(of(log['from']))` (the promise rejects before subscribe) and
    // surfaces as an unhandled promise rejection on page load.
    if (!address || typeof address !== 'string' || !isAddress(address, { strict: false })) {
      return '';
    }
    const result = await readContract(this.appkitService.adapter.wagmiConfig, {
      address: getAddress(address),
      abi: erc20Abi,
      chainId,
      functionName: 'name',
      args: []
    });
    return result as unknown as string;
  }

  async getBalance(chainId: number, tokenAddress: string, account: string) {
    console.log(tokenAddress, "tokenAddress")
    let balance: any = await readContract(this.appkitService.adapter.wagmiConfig, {
      address: await getAddress(tokenAddress),
      abi: erc20Abi,
      chainId,
      functionName: 'balanceOf',
      args: [account]
    });
    return balance.toString();
  }

  async getAllowance(chainId: number, tokenAddress: string, owner: string, spender: string) {
    return await readContract(this.appkitService.adapter.wagmiConfig, {
      address: await getAddress(tokenAddress),
      abi: erc20Abi,
      chainId,
      functionName: 'allowance',
      args: [owner, spender]
    });
  }

  async transfer({ chainId, walletAddress }: any, { functionName, args }: any, contractAddress: string,) {
    let contractParams = {
      abi: erc20Abi,
      functionName,
      args
    }
    let requiredGas = await this.publicClient?.estimateContractGas({
      address: await getAddress(contractAddress),
      account: walletAddress,
      ...contractParams
    });
    let transferAbi = await encodeFunctionData({
      ...contractParams
    })
    return { transferAbi, requiredGas }
  }
}
